import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import {
  AppointmentDuration,
  AppointmentType,
  FLOW_MODES,
} from '@avanta/shared/constants/appointments';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router';
import { useFetchActivePatientRecord } from 'hooks/useFetchActivePatientRecord';
import { type PatientRecord } from 'types/patient';
import { TopNav } from './components/ui/TopNav';
import { useBookAppointment } from './hooks/useBookAppointment';
import { useEditAppointment } from './hooks/useEditAppointment';
import { useStepNavigation } from './hooks/useStepNavigation';
import { AppointmentsReservePayload, type AppointmentsReserveFormData } from './types';
import { getRecordFieldValues } from './utils';

interface AppointmentsReserveFormProviderProps {
  patientId: string;
  patientRecord?: PatientRecord | null;
}

export const AppointmentsReserveForm = (): React.ReactElement => {
  const { handleSubmit, watch } = useFormContext<AppointmentsReserveFormData>();
  const { steps, currentStepIndex, currentStep, handleNext, handleBack } = useStepNavigation();
  const StepComponent = currentStep.component;
  const { mutate: bookAppointment } = useBookAppointment();
  const { mutate: editAppointment } = useEditAppointment();
  const { state } = useLocation();
  const flow = watch(AppointmentsReservePayload.flow);

  const onSubmit = (data: AppointmentsReserveFormData): void => {
    if (flow === FLOW_MODES.edit) {
      editAppointment({
        patientId: data.patientId,
        psychologistId: data.psychologist?.id ?? '',
        previousDate: state?.appointment.dateTime,
        newDate: data.dateTime,
        reason: data.observations,
      });
      return;
    }
    const payload = {
      psychologistId: data.psychologist?.id ?? '',
      patientId: data.patientId,
      dateTime: data.dateTime,
      contactMethod: AppointmentType.InPerson,
      durationType: AppointmentDuration.DURATION_DOUBLE,
      backofficeCenterId: Number(data.backofficeCenterId),
      specialityBooked: Number(data.specialityBooked),
      psychologistServiceBooked: Number(data.psychologistServiceBooked),
      observations: data.observations ?? '',
      contractId: Number(data.contractId),
      contractRateId: Number(data.contractRateId),
      contractAuthorization: data.contractAuthorization,
      recordFields: data.recordFields,
    };
    bookAppointment({ payload, document: data.document });
  };

  return (
    <div className="flex flex-col h-dvh overflow-hidden relative">
      <TopNav steps={steps} currentStepIndex={currentStepIndex + 1} />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="scrollbar-custom mx-auto flex min-h-0 flex-1 flex-col overflow-y-auto pb-4"
      >
        <StepComponent handleNext={handleNext} handleBack={handleBack} />
      </form>
    </div>
  );
};

const AppointmentsReserveFormProvider = ({
  patientId,
  patientRecord,
}: AppointmentsReserveFormProviderProps): React.ReactElement => {
  const { state } = useLocation();
  const flow = state?.flow ?? FLOW_MODES.regular;
  const methods = useForm<AppointmentsReserveFormData>({
    defaultValues: {
      flow: flow,
      patientId,
      contractId: patientRecord?.contract ? String(patientRecord.contract.id) : '',
      contractRateId: patientRecord?.selectedRate ? String(patientRecord.selectedRate.id) : '',
      recordFields: getRecordFieldValues(patientRecord?.values),
      hasExpedient: patientRecord?.code ? true : false,
      ...(state?.appointment && {
        city: state?.appointment?.center?.city ?? '',
        backofficeCenterId: String(state?.appointment?.center?.id ?? ''),
        dateTime: state?.appointment?.dateTime ?? '',
        specialityBooked: state?.appointment?.specialitiesBookable ?? '',
        psychologistServiceBooked: state?.appointment?.psychologistServiceBooked ?? '',
        observations: state?.appointment?.observations ?? '',
        contractAuthorization: state?.appointment?.contractAuthorization ?? '',
        document: state?.appointment?.document ?? '',
        psychologist: state?.psychologist ?? undefined,
      }),
    },
  });
  return (
    <FormProvider {...methods}>
      <AppointmentsReserveForm />
    </FormProvider>
  );
};

export const AppointmentsReserve = (): React.ReactElement => {
  const location = useLocation();
  const patientId = location.state?.patientId ?? '';
  const { data: patientRecord, isLoading } = useFetchActivePatientRecord(patientId);

  if (isLoading) return <LoadingScreen />;

  return <AppointmentsReserveFormProvider patientId={patientId} patientRecord={patientRecord} />;
};
